// controllers/regionSelectController.js
//
// Populates the light-KYC state/region select from whichever country is picked
// in the country field above it (COI-840). The full country -> regions map is
// rendered onto the element as JSON rather than fetched, so switching country
// costs no round trip.
//
// The select is the customer-facing convenience only. SubmitLightKYC re-checks
// the country/region pair server-side, because both arrive as form fields a
// client can set to anything.
import { Controller } from "@hotwired/stimulus"

export default class RegionSelectController extends Controller {
  static targets = ["select"]
  static values = {
    regions: Object,
    countryField: String,
    placeholder: String,
    emptyPlaceholder: String,
  }

  connect() {
    this.countryInput = this.findCountryInput()
    if (this.countryInput) {
      this.boundCountryChanged = this.countryChanged.bind(this)
      this.countryInput.addEventListener("change", this.boundCountryChanged)
    }
    this.render()
  }

  disconnect() {
    if (this.countryInput && this.boundCountryChanged) {
      this.countryInput.removeEventListener("change", this.boundCountryChanged)
    }
  }

  countryChanged() {
    this.render()
  }

  // The country picker lives in a sibling element, so scope the lookup to the
  // enclosing form rather than the controller element.
  findCountryInput() {
    const root = this.element.closest("form") || document
    return root.querySelector(`[name="${this.countryFieldValue}"]`)
  }

  render() {
    if (!this.hasSelectTarget) return

    const country = this.countryInput ? this.countryInput.value : ""
    const regions = this.regionsValue[country] || []
    const previous = this.selectTarget.value

    // Rebuilding wipes the previous selection on purpose: a state only means
    // something under the country it belongs to, and carrying "California" over
    // to a new country would submit a pair the server refuses.
    this.selectTarget.replaceChildren()

    const placeholder = document.createElement("option")
    placeholder.value = ""
    placeholder.textContent = regions.length
      ? this.placeholderValue
      : this.emptyPlaceholderValue
    this.selectTarget.appendChild(placeholder)

    for (const region of regions) {
      const option = document.createElement("option")
      option.value = region
      option.textContent = region
      this.selectTarget.appendChild(option)
    }

    this.selectTarget.value = regions.includes(previous) ? previous : ""
    this.selectTarget.disabled = regions.length === 0

    // form-validation gates the submit button on this field, and it only
    // listens — nothing dispatches on a programmatic value change.
    this.selectTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }
}
